import { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Skeleton,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import GlassCard from "../../General/GlassCard";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import { colorForStage } from "../../../config/legalFileStages";

const OPERATIONS_LIMIT_OPTIONS = [
  { value: 0, label: "Todas las operaciones" },
  { value: 5, label: "Últimas 5 operaciones" },
  { value: 3, label: "Últimas 3 operaciones" },
  { value: 2, label: "Últimas 2 operaciones" },
  { value: 1, label: "Última operación" },
];

function CheckboxList({ options, selected, onToggle, getLabel, getKey, getDotColor, emptyText }) {
  if (options.length === 0) {
    return (
      <Typography variant="caption" sx={{ opacity: 0.6 }}>
        {emptyText}
      </Typography>
    );
  }

  return (
    <Box sx={{ maxHeight: 220, overflowY: "auto", pr: 0.5 }}>
      {options.map((option) => {
        const key = getKey(option);
        const checked = selected.includes(key);
        return (
          <FormControlLabel
            key={key}
            sx={{ display: "flex", ml: 0, mr: 0 }}
            control={
              <Checkbox
                size="small"
                checked={checked}
                onChange={() => onToggle(key)}
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                {getDotColor && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: getDotColor(option),
                      flexShrink: 0,
                    }}
                  />
                )}
                <Typography sx={{ fontSize: 13.5 }}>{getLabel(option)}</Typography>
              </Box>
            }
          />
        );
      })}
    </Box>
  );
}

function FilterSection({ title, count = 0, defaultExpanded = false, children }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Accordion
      disableGutters
      square
      elevation={0}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={{
        background: "transparent",
        "&:before": { display: "none" },
        "&:not(:last-of-type)": {
          borderBottom: (theme) =>
            `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon />}
        sx={{ px: 0, minHeight: 44, "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1, my: 1 } }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: 13, opacity: 0.85 }}>
          {title}
        </Typography>
        {count > 0 && (
          <Chip
            label={count}
            size="small"
            color="primary"
            sx={{ height: 18, "& .MuiChip-label": { px: 0.8, fontSize: 11, fontWeight: 800 } }}
          />
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0, pb: 1.5 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

export default function TaskFilters({
  systemStates,
  lawyers,
  stages,
  loadingStates,
  loadingLawyers,
  loadingStages,
  selectedStates,
  onToggleState,
  selectedLawyerIds,
  onToggleLawyer,
  selectedStageIds,
  onToggleStage,
  operationsLimit,
  onChangeOperationsLimit,
}) {
  return (
    <GlassCard>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <FilterAltRoundedIcon color="primary" />
        <Typography sx={{ fontWeight: 900, fontSize: 15 }}>Filtros</Typography>
      </Box>

      <FilterSection title="Estado del sistema" count={selectedStates.length}>
        {loadingStates ? (
          <Skeleton variant="rounded" height={80} sx={{ borderRadius: 2 }} />
        ) : (
          <CheckboxList
            options={systemStates}
            selected={selectedStates}
            onToggle={onToggleState}
            getKey={(o) => o.key ?? o.id ?? o.code ?? o.name}
            getLabel={(o) => o.name ?? o}
            emptyText="No hay estados disponibles."
          />
        )}
      </FilterSection>

      <FilterSection title="Etapa del expediente" count={selectedStageIds.length}>
        {loadingStages ? (
          <Skeleton variant="rounded" height={80} sx={{ borderRadius: 2 }} />
        ) : (
          <CheckboxList
            options={stages}
            selected={selectedStageIds}
            onToggle={onToggleStage}
            getKey={(o) => o.id}
            getLabel={(o) => o.name}
            getDotColor={(o) => colorForStage(o)}
            emptyText="No hay etapas disponibles."
          />
        )}
      </FilterSection>

      <FilterSection title="Abogado" count={selectedLawyerIds.length}>
        {loadingLawyers ? (
          <Skeleton variant="rounded" height={80} sx={{ borderRadius: 2 }} />
        ) : (
          <CheckboxList
            options={lawyers}
            selected={selectedLawyerIds}
            onToggle={onToggleLawyer}
            getKey={(o) => o.id}
            getLabel={(o) => `${o.name} ${o.lastname}`}
            emptyText="No hay abogados disponibles."
          />
        )}
      </FilterSection>

      <FilterSection title="Cantidad de operaciones">
        <RadioGroup
          value={operationsLimit}
          onChange={(e) => onChangeOperationsLimit(Number(e.target.value))}
        >
          {OPERATIONS_LIMIT_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              control={<Radio size="small" />}
              label={<Typography sx={{ fontSize: 13.5 }}>{opt.label}</Typography>}
            />
          ))}
        </RadioGroup>
      </FilterSection>
    </GlassCard>
  );
}
